import React, {
  memo,
  useRef,
  useImperativeHandle,
  forwardRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { Container, Sprite, Graphics } from "@pixi/react";
import * as PIXI from "pixi.js";
import { Settings } from "../../../settings";
import { OVERLAY_BACKGROUND_COLOR } from "../Overlay/OverlayController";
import { FEATURES_CONFIG } from "../../../featuresConfig";

// Dimmed symbol alpha (calculated from tint 0x555555)
const DIMMED_ALPHA = 0.75;

// Unified dimming overlay for both main game and free spins
const DimReelSet = memo(
  forwardRef(({ symbolWidth, symbolHeight, dividerDimensions }, ref) => {
    const dimSpriteRef = useRef(null);
    const currentAlphaRef = useRef(0); // Track current alpha to persist across re-renders

    // Memoize sprite dimensions
    const spriteDimensions = useMemo(
      () => ({
        width:
          symbolWidth * Settings.REEL_COUNT +
          dividerDimensions.width * (Settings.REEL_COUNT - 1),
        height: symbolHeight * Settings.SYMBOLS_VISIBLE * 1.03,
      }),
      [symbolWidth, symbolHeight, dividerDimensions.width],
    );

    // Expose dimming control methods through ref
    useImperativeHandle(
      ref,
      () => ({
        // Dim the reels (show overlay on non-winning symbols)
        dimReels: () => {
          if (!dimSpriteRef.current) return;
          currentAlphaRef.current = DIMMED_ALPHA;
          dimSpriteRef.current.alpha = DIMMED_ALPHA;
        },

        // Remove dimming (clear overlay)
        undimReels: () => {
          if (!dimSpriteRef.current) return;
          currentAlphaRef.current = 0;
          dimSpriteRef.current.alpha = 0;
        },
      }),
      [],
    );

    // Preserve alpha state across re-renders (especially during window resize)
    useEffect(() => {
      if (dimSpriteRef.current && currentAlphaRef.current !== 0) {
        // Restore the alpha without animation when dimensions change
        dimSpriteRef.current.alpha = currentAlphaRef.current;
      }
    }, [symbolWidth, symbolHeight, dividerDimensions]);

    // Draw 1px yellow debug border (centered to match Sprite anchor 0.5)
    const drawBorder = useCallback(
      (g) => {
        g.clear();
        g.lineStyle(1, 0xffff00, 1);
        g.drawRect(
          -spriteDimensions.width / 2,
          -spriteDimensions.height / 2,
          spriteDimensions.width,
          spriteDimensions.height,
        );
      },
      [spriteDimensions.width, spriteDimensions.height],
    );

    return (
      <Container name="dim_overlay" zIndex={2}>
        {FEATURES_CONFIG.DEV_TOOLS.DEBUG_BORDER && (
          <Graphics draw={drawBorder} />
        )}
        <Sprite
          ref={dimSpriteRef}
          texture={PIXI.Texture.WHITE}
          width={spriteDimensions.width}
          height={spriteDimensions.height}
          anchor={0.5}
          tint={OVERLAY_BACKGROUND_COLOR}
          alpha={0}
          name="dim_overlay_placeholder"
        />
      </Container>
    );
  }),
);

DimReelSet.displayName = "DimReelSet";

export default DimReelSet;
